import { FileWithPath } from "@mantine/dropzone";
import { IconCheck, IconUpload, IconX } from "@tabler/icons-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v1/colors";
import { radius } from "#app/theme/v1/radius";
import { shadows } from "#app/theme/v1/shadows";
import { spacing } from "#app/theme/v1/spacing";
import { Box } from "#shared/view/components/Box";
import { Container } from "#shared/view/components/Container";
import { Dropzone } from "#shared/view/components/Dropzone";
import { Flex } from "#shared/view/components/Flex";
import { Paper } from "#shared/view/components/Paper";
import { ProgressBar } from "#shared/view/components/ProgressBar";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";
import { TextInput } from "#shared/view/designSystem/inputs/TextInput";
import { getAccept, UPLOAD_CONFIG } from "shared";

import { useOnboardingUpload } from "./hooks/useOnboardingUpload";

enum OnboardingStep {
  HOME = "home",
  AUTH = "auth",
  UPLOAD = "upload",
  SUCCESS = "success",
}

const getRelativePath = (file: FileWithPath) =>
  file.webkitRelativePath || file.relativePath || file.path || file.name;

const getRootDir = (file: FileWithPath): string | null => {
  const rel = String(getRelativePath(file)).replace(/\\/g, "/");
  const parts = rel.split("/");

  return parts.length > 1 ? (parts[0] ?? null) : null;
};

type DirItem = { type: "dir"; name: string; files: FileWithPath[] };
type FileItem = { type: "file"; name: string; file: FileWithPath };
type DisplayItem = DirItem | FileItem;

export const OnboardingPage = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(OnboardingStep.HOME);
  const [email, setEmail] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [emailError, setEmailError] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [showProgress, setShowProgress] = useState(false);

  const dirInputRef = useRef<HTMLInputElement>(null);

  const { uploadFiles, isUploading, uploadProgress, resetUpload } = useOnboardingUpload();

  const validateEmail = (mail: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return emailRegex.test(mail);
  };

  const handleAuth = useCallback(async () => {
    setEmailError("");
    setCompanyError("");

    if (companyId.trim()) {
      try {
        setCurrentStep(OnboardingStep.UPLOAD);
      } catch {
        setCompanyError(t("onboarding.errors.faberaIdVerification"));
      }
    } else if (email.trim() && validateEmail(email)) {
      setCurrentStep(OnboardingStep.UPLOAD);
    } else {
      if (!email.trim() && !companyId.trim()) {
        setEmailError(t("onboarding.errors.emailOrIdRequired"));
      } else if (email.trim() && !validateEmail(email)) {
        setEmailError(t("onboarding.errors.validEmailRequired"));
      }
    }
  }, [email, companyId, t]);

  const handleFileDrop = useCallback((droppedFiles: FileWithPath[]) => {
    setFiles((prevFiles) => [...prevFiles, ...droppedFiles]);
  }, []);

  const handleDirectorySelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []) as FileWithPath[];
    if (selected.length) {
      setFiles((prev) => [...prev, ...selected]);
    }
    e.target.value = "";
  }, []);

  const items: DisplayItem[] = useMemo(() => {
    const byRoot = new Map<string, FileWithPath[]>();
    const singles: FileItem[] = [];

    files.forEach((f) => {
      const root = getRootDir(f);
      if (root) {
        const arr = byRoot.get(root) ?? [];
        arr.push(f);
        byRoot.set(root, arr);
      } else {
        singles.push({ type: "file", name: f.name, file: f });
      }
    });

    const dirItems: DirItem[] = Array.from(byRoot.entries()).map(([name, groupFiles]) => ({
      type: "dir",
      name,
      files: groupFiles,
    }));

    return [...dirItems, ...singles];
  }, [files]);

  const removeItem = useCallback((item: DisplayItem) => {
    if (item.type === "dir") {
      setFiles((prev) => prev.filter((f) => getRootDir(f) !== item.name));
    } else {
      setFiles((prev) => prev.filter((f) => f !== item.file));
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (files.length === 0) {
      return;
    }

    setShowProgress(true);

    try {
      // ⚠️ The hool must use webkitRelativePath / relativePath to keep the hierarchy
      await uploadFiles(files, email, companyId);
      setCurrentStep(OnboardingStep.SUCCESS);
    } catch (_error) {
      setCurrentStep(OnboardingStep.UPLOAD);
      setShowProgress(false);
    }
  }, [files, email, companyId, uploadFiles]);

  const resetForm = useCallback(() => {
    setCurrentStep(OnboardingStep.HOME);
    setEmail("");
    setCompanyId("");
    setFiles([]);
    setShowProgress(false);
    setEmailError("");
    setCompanyError("");
    resetUpload();
  }, [resetUpload]);

  const renderHomeStep = () => (
    <Stack gap="md">
      <Flex align="center" justify="center" mb="md">
        <img alt="Fabera Logo" src="/logo-fabera-login.png" width="139" height="60" />
      </Flex>

      <Text size="xl" fw={600} ta="center" mb="xs">
        {t("onboarding.title")}
      </Text>

      <Box p="md" style={{ backgroundColor: colors.blue[0], borderRadius: radius.sm }}>
        <Text fw={500} mb="xs" size="md">
          {t("onboarding.home.description")}
        </Text>
        <ul style={{ margin: 0, paddingLeft: spacing.md, fontSize: "1rem" }}>
          <li>{t("onboarding.home.plans3D")}</li>
          <li>{t("onboarding.home.plans2D")}</li>
          <li>{t("onboarding.home.costFactors")}</li>
        </ul>
        <Text mt="xs" size="md">
          {t("onboarding.home.secureUpload")}
        </Text>
      </Box>

      <Button size="lg" onClick={() => setCurrentStep(OnboardingStep.AUTH)} fullWidth>
        {t("onboarding.home.submitData")}
      </Button>
    </Stack>
  );

  const renderAuthStep = () => (
    <Stack gap="md">
      <Flex align="center" justify="center" mb="md">
        <img alt="Fabera Logo" src="/logo-fabera-login.png" width="139" height="60" />
      </Flex>

      <Text size="xl" fw={600} ta="center" mb="xs">
        {t("onboarding.auth.title")}
      </Text>

      <Text>{t("onboarding.auth.withEmail")}</Text>
      <TextInput
        placeholder={t("onboarding.auth.emailPlaceholder")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={emailError}
        type="email"
        mt={2}
      />

      <Text ta="center" c="dimmed">
        {t("onboarding.auth.or")}
      </Text>

      <Text>{t("onboarding.auth.withFaberaId")}</Text>
      <TextInput
        placeholder={t("onboarding.auth.faberaIdPlaceholder")}
        value={companyId}
        onChange={(e) => setCompanyId(e.target.value)}
        error={companyError}
        mt={2}
      />

      <Button size="lg" onClick={handleAuth} fullWidth mt="md">
        {t("onboarding.auth.continue")}
      </Button>
    </Stack>
  );

  const renderUploadStep = () => (
    <Stack gap="md">
      <Flex align="center" justify="center" mb="md">
        <img alt="Fabera Logo" src="/logo-fabera-login.png" width="139" height="60" />
      </Flex>

      <Text size="xl" fw={600} ta="center" mb="xs">
        {t("onboarding.upload.title")}
      </Text>

      {!showProgress && (
        <>
          <Dropzone
            onDrop={handleFileDrop}
            title={t("onboarding.upload.dropzoneTitle")}
            loading={isUploading}
            subTitle={""}
            maxSize={UPLOAD_CONFIG.ONBOARDING.maxFileSize}
            accept={getAccept(UPLOAD_CONFIG.ONBOARDING.extensions)}
          />

          <Flex justify="center" mt="xs" gap="sm">
            <Button
              variant="outline"
              onClick={() => dirInputRef.current?.click()}
              disabled={isUploading}
            >
              {t("onboarding.upload.importFolder")}
            </Button>
            <input
              ref={dirInputRef}
              type="file"
              multiple
              // @ts-expect-error: not typed but supported by Chromium/Safari
              webkitdirectory="true"
              onChange={handleDirectorySelect}
              style={{ display: "none" }}
            />
          </Flex>

          {items.length > 0 && (
            <Box mt="md">
              <Text fw={500} mb="xs">
                {t("onboarding.upload.selectedFiles", { count: items.length })}
              </Text>
              <Stack gap="xs">
                {items.map((item, index) => {
                  const label = item.type === "dir" ? `${item.name}/` : item.name;

                  return (
                    <Flex
                      key={`${item.type}-${label}-${index}`}
                      justify="space-between"
                      align="center"
                      p="xs"
                      style={{
                        backgroundColor: colors.gray[1],
                        borderRadius: radius.sm,
                      }}
                    >
                      <Text size="sm">
                        {item.type === "dir" ? `${t("onboarding.upload.folder")}: ${label}` : label}
                      </Text>
                      <IconX
                        size={16}
                        style={{ cursor: "pointer", color: colors.red[6] }}
                        onClick={() => removeItem(item)}
                      />
                    </Flex>
                  );
                })}
              </Stack>
            </Box>
          )}

          <Button
            size="lg"
            onClick={handleUpload}
            disabled={files.length === 0 || isUploading}
            loading={isUploading}
            fullWidth
            mt="md"
          >
            {t("onboarding.upload.uploadFiles")}
          </Button>

          {items.length > 0 && (
            <Text size="sm" c="dimmed" ta="center">
              {t("onboarding.upload.removeHint")}
            </Text>
          )}
        </>
      )}

      {showProgress && (
        <Stack gap="md" align="center">
          <Text fw={500} ta="center">
            {t("onboarding.upload.uploadProgress")}
          </Text>

          <Box w="100%">
            <ProgressBar value={uploadProgress} label={`${uploadProgress}%`} animated />
          </Box>

          <Button
            size="lg"
            disabled={uploadProgress < 100}
            onClick={() => setCurrentStep(OnboardingStep.SUCCESS)}
            fullWidth
            variant={uploadProgress === 100 ? "filled" : "outline"}
            leftSection={
              uploadProgress === 100 ? <IconCheck size={16} /> : <IconUpload size={16} />
            }
          >
            {uploadProgress === 100
              ? t("onboarding.upload.finish")
              : t("onboarding.upload.uploading")}
          </Button>
        </Stack>
      )}
    </Stack>
  );

  const renderSuccessStep = () => (
    <Stack gap="md" align="center">
      <Flex align="center" justify="center" mb="md">
        <img alt="Fabera Logo" src="/logo-fabera-login.png" width="139" height="60" />
      </Flex>

      <IconCheck size={64} color={colors.green[6]} />

      <Text size="xl" fw={600} ta="center">
        {t("onboarding.success.title")}
      </Text>

      <Text ta="center" c="dimmed" size="lg">
        {t("onboarding.success.message")}
      </Text>

      <Button size="lg" onClick={resetForm} fullWidth mt="md" variant="outline">
        {t("onboarding.success.newSession")}
      </Button>
    </Stack>
  );

  const getCurrentStepContent = () => {
    switch (currentStep) {
      case OnboardingStep.HOME:
        return renderHomeStep();
      case OnboardingStep.AUTH:
        return renderAuthStep();
      case OnboardingStep.UPLOAD:
        return renderUploadStep();
      case OnboardingStep.SUCCESS:
        return renderSuccessStep();
      default:
        return renderHomeStep();
    }
  };

  return (
    <div style={styles.pageWrapper}>
      <Container size="sm" py="xl" style={styles.container}>
        <Paper style={styles.paper}>
          <Box style={styles.contentBox}>{getCurrentStepContent()}</Box>
        </Paper>
      </Container>
    </div>
  );
};

const styles = {
  pageWrapper: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column" as const,
    background: "var(--Blue-gradient, linear-gradient(103deg, #2955E2 1.95%, #0B267B 91.53%))",
    padding: spacing.md,
  },
  container: {
    display: "flex",
    flexDirection: "column" as const,
    flexGrow: 1,
  },
  paper: {
    minHeight: "400px",
    padding: spacing.xl,
    borderRadius: radius.md,
    background: "white",
    boxShadow: shadows.md,
  },
  contentBox: {
    maxWidth: 500,
    margin: "0 auto",
  },
};
